/*
 * Design: Swiss Precision × 和の余白
 * AEO対策: 中四国DX・AIセミナー・研修の地域特化セクション
 * 構造化データのCourse/Eventと連動するコンテンツ
 * Mobile: カード余白最適化、タッチターゲット48px
 */

import { motion } from "framer-motion";
import { scrollToContactForm } from "@/hooks/useScrollToContact";
import {
  Presentation,
  GraduationCap,
  MapPin,
  Calendar,
  Users,
  ArrowRight,
  CheckCircle2,
  Building2,
  Monitor,
  BadgePercent,
} from "lucide-react";

const SEMINAR_TYPES = [
  {
    icon: Presentation,
    badge: "SEMINAR",
    title: "DX・AI活用セミナー",
    description:
      "中小〜中堅企業の経営者・DX推進担当者を対象に、AI業務自動化・DX推進の実践ノウハウをお伝えします。2026年のAIトレンドから具体的な導入事例まで、すぐに活かせる内容です。",
    details: [
      "DXの基本概念と2026年のAIトレンド",
      "Google Workspace × 生成AIの実践活用法",
      "AI業務自動化の具体的な導入事例",
      "補助金・助成金を活用したDX推進の進め方",
    ],
    format: "対面（広島・中四国各地）/ オンライン",
    duration: "60〜90分",
    price: "無料（事前予約制）",
    audience: "経営者・DX推進担当者・管理職",
  },
  {
    icon: GraduationCap,
    badge: "TRAINING",
    title: "DX人材育成研修プログラム",
    description:
      "非IT社員でもAIエージェントを構築・運用できるスキルを習得する実践型研修。Google WorkspaceとAIを活用した業務自動化を、座学と実習で身につけます。",
    details: [
      "AIエージェントの基本概念と業務への適用",
      "Google Workspace活用の業務自動化設計・実装",
      "KPI設計と改善サイクルの運用方法",
      "内製化・自走体制の構築手法",
    ],
    format: "出張研修（中四国全域対応）/ オンライン",
    duration: "3ヶ月（集合研修＋OJT伴走）",
    price: "150万円〜（助成金で最大75%助成）",
    audience: "非IT社員・現場リーダー・管理職",
  },
];

const COVERAGE_AREAS = [
  { region: "広島県", cities: "広島市・福山市・呉市・東広島市ほか", primary: true },
  { region: "岡山県", cities: "岡山市・倉敷市ほか", primary: true },
  { region: "山口県", cities: "下関市・山口市ほか", primary: true },
  { region: "島根県", cities: "松江市・出雲市ほか", primary: false },
  { region: "鳥取県", cities: "鳥取市・米子市ほか", primary: false },
  { region: "香川県", cities: "高松市ほか", primary: false },
  { region: "愛媛県", cities: "松山市ほか", primary: false },
  { region: "徳島県", cities: "徳島市ほか", primary: false },
  { region: "高知県", cities: "高知市ほか", primary: false },
];

const SEMINAR_FEATURES = [
  {
    icon: MapPin,
    title: "中四国全域に出張対応",
    description: "広島を拠点に、中四国9県への出張セミナー・研修に対応。御社の会議室での開催も可能です。",
  },
  {
    icon: Monitor,
    title: "オンライン対応",
    description: "Google Meetを活用したオンラインセミナー・研修にも対応。遠方の方も気軽にご参加いただけます。",
  },
  {
    icon: BadgePercent,
    title: "助成金で費用を軽減",
    description: "人材開発支援助成金を活用すれば、研修費用の最大75%が助成されます。申請手続きもサポート。",
  },
  {
    icon: Building2,
    title: "広島商工会議所と連携",
    description: "広島商工会議所との連携セミナーも開催。地域の中小企業ネットワークを活かした情報提供を行います。",
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function SeminarSection() {
  return (
    <section id="seminar-section" className="py-14 sm:py-20 lg:py-28 bg-[#F9F9F9]">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-16">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Seminar & Training
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            DXセミナー・研修
            <span className="block text-sm sm:text-base lg:text-lg font-medium text-[#666666] mt-1 sm:mt-2">
              広島・中四国エリアを中心に開催
            </span>
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-3xl leading-relaxed">
            「AIって何ができるの？」「うちの会社でもDXは必要？」——そんな疑問にお答えする、
            経営者・現場リーダー向けのDX・AIセミナーと、実践型の人材育成研修を提供しています。
            ITリテラシーに自信がない方でも安心してご参加いただける内容です。
          </p>
        </motion.div>

        {/* Seminar Types */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 mb-10 sm:mb-16">
          {SEMINAR_TYPES.map((seminar, i) => (
            <motion.div
              key={seminar.title}
              {...fadeInUp}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="bg-white border border-[#E0E0E0] hover:border-[#E03030] transition-all duration-300 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
            >
              <div className="p-5 sm:p-6 lg:p-8">
                {/* Badge */}
                <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-5">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-[#1A1A1A] text-white rounded-sm">
                    <seminar.icon size={16} strokeWidth={1.5} />
                  </div>
                  <span
                    className="text-[10px] font-bold tracking-widest text-[#666666]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {seminar.badge}
                  </span>
                </div>

                {/* Title & Description */}
                <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-2 sm:mb-3">
                  {seminar.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#333333] leading-relaxed mb-4 sm:mb-5">
                  {seminar.description}
                </p>

                {/* Details */}
                <div className="mb-4 sm:mb-5">
                  <p className="text-[10px] sm:text-xs font-bold text-[#666666] uppercase tracking-wider mb-2 sm:mb-3">
                    主なテーマ
                  </p>
                  <ul className="space-y-1.5 sm:space-y-2">
                    {seminar.details.map((detail) => (
                      <li key={detail} className="flex items-start gap-2 text-xs sm:text-sm text-[#333333]">
                        <CheckCircle2 size={14} className="shrink-0 text-[#E03030] mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 bg-[#F9F9F9] border border-[#E0E0E0] p-3 sm:p-4">
                  <div>
                    <p className="text-[10px] text-[#666666] mb-0.5">形式</p>
                    <p className="text-xs sm:text-sm font-medium text-[#1A1A1A]">{seminar.format}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#666666] mb-0.5">期間</p>
                    <p className="text-xs sm:text-sm font-medium text-[#1A1A1A]">{seminar.duration}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#666666] mb-0.5">費用</p>
                    <p className="text-xs sm:text-sm font-medium text-[#E03030]">{seminar.price}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#666666] mb-0.5">対象</p>
                    <p className="text-xs sm:text-sm font-medium text-[#1A1A1A]">{seminar.audience}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.1 }} className="mb-10 sm:mb-16">
          <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-5 sm:mb-8">
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666] mr-2 sm:mr-3"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Features
            </span>
            セミナー・研修の特徴
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {SEMINAR_FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                className="p-4 sm:p-5 bg-white border border-[#E0E0E0] hover:border-[#E03030] transition-all duration-300"
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-[#FEF2F2] text-[#E03030] rounded-sm mb-3 sm:mb-4">
                  <feature.icon size={16} strokeWidth={1.5} />
                </div>
                <h4 className="text-xs sm:text-sm font-bold text-[#1A1A1A] mb-1.5 sm:mb-2 leading-snug">
                  {feature.title}
                </h4>
                <p className="text-[11px] sm:text-xs text-[#333333] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Coverage Area */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.2 }}>
          <div className="bg-white border border-[#E0E0E0] p-4 sm:p-6 lg:p-8">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <MapPin size={18} className="text-[#E03030]" />
              <h3 className="text-sm sm:text-base font-bold text-[#1A1A1A]">対応エリア</h3>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-9 gap-2 sm:gap-3 mb-4 sm:mb-6">
              {COVERAGE_AREAS.map((area) => (
                <div
                  key={area.region}
                  className={`text-center p-2 sm:p-3 border rounded-sm ${
                    area.primary
                      ? "border-[#E03030] bg-[#FEF2F2]"
                      : "border-[#E0E0E0] bg-[#F9F9F9]"
                  }`}
                >
                  <p className={`text-xs sm:text-sm font-bold ${area.primary ? "text-[#E03030]" : "text-[#1A1A1A]"}`}>
                    {area.region}
                  </p>
                  <p className="text-[9px] sm:text-[10px] text-[#666666] mt-0.5">{area.cities}</p>
                </div>
              ))}
            </div>
            <p className="text-xs sm:text-sm text-[#666666]">
              上記エリア以外でも、大阪・東京・沖縄を含む全国で対応可能です。オンライン開催もお気軽にご相談ください。
            </p>

            {/* CTA */}
            <div className="mt-5 sm:mt-6 pt-4 sm:pt-5 border-t border-[#E0E0E0] flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <button
                onClick={() => scrollToContactForm()}
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-2.5 bg-[#E03030] text-white text-sm font-bold rounded hover:bg-[#c72828] transition-colors active:scale-[0.98] touch-manipulation"
              >
                セミナー・研修のご相談
                <ArrowRight size={14} />
              </button>
              <span className="text-xs text-[#666666]">
                無料相談受付中（約60分のヒアリング）
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
